import {type ComponentProps, type ReactNode, useId, useLayoutEffect, useRef, useState} from 'react';
import {Ban, CheckCircle2, ChevronRight} from 'lucide-react';
import {
  Badge,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Item,
  ItemContent,
  ItemDescription,
  ItemTitle
} from '@kroll-resolver-crisp/ui-components-react';
import {cn} from '@/lib/utils';
import {toneBadge, visibilityTone} from '@/lib/status';
import {StatusIcon} from '@/components/shared/StatusIcon';

// Pieces shared by the Single Submission page's status cards (VideoStatusCard, ChannelStatusCard,
// PinterestProfileCard). All of them render the same three-state boolean, the same date format,
// the same eyebrow-label meta grid and the same "More details" expander, so they live here once
// rather than drifting across three files.

export function BoolValue({value}: {readonly value: boolean | null}) {
  if (value === null) {
    return <span className="text-muted-foreground">Unknown</span>;
  }

  // "Yes" is a status word, so it takes the status badge rather than --success as ink, which is a
  // surface token and fails AA as text.
  return value ? (
    <Badge {...toneBadge('success')}>
      <CheckCircle2 /> Yes
    </Badge>
  ) : (
    <span className="text-muted-foreground flex items-center gap-1.5">
      <Ban className="size-3.5" /> No
    </span>
  );
}

export function formatDate(iso: string | null): string {
  if (!iso) {
    return '—';
  }

  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

// The platform's own visibility state for the entity, as the shared icon beside the word - colour
// is never the only carrier, and an unrecognised value still reads, just without a tone.
export function PlatformStatus({status}: {readonly status: string | null}) {
  if (!status) {
    return <span className="text-muted-foreground">—</span>;
  }

  return (
    <span className="flex items-center gap-1.5">
      <StatusIcon tone={visibilityTone(status)} />
      {status}
    </span>
  );
}

// The investigations expander idiom (see ItemMeta) at the status cards' own type scale. Owns the
// open state so each card only supplies the panel. Collapsible wires the aria-expanded/aria-controls
// pair itself - the panel it opens is the only place several of these fields appear, so a screen
// reader that can't tell the toggle is a toggle can't reach them at all.
export function MoreDetails({
  children,
  className
}: {
  readonly children: ReactNode;
  readonly className?: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Collapsible open={open} className={className} onOpenChange={setOpen}>
      <CollapsibleTrigger
        render={
          <Button type="button" variant="ghost" size="xs" className="text-muted-foreground -ml-2" />
        }>
        <ChevronRight
          aria-hidden
          className="size-3 transition-transform in-data-panel-open:rotate-90"
        />
        {open ? 'Less' : 'More details'}
      </CollapsibleTrigger>
      <CollapsibleContent>{children}</CollapsibleContent>
    </Collapsible>
  );
}

// A description clamped to two lines, with a "See more"/"See less" toggle that only appears when
// the text actually overflows the clamp (measured via scrollHeight vs clientHeight, since the
// clamp is CSS-only and the text length varies per entity). Shared so ChannelStatusCard and
// VideoStatusCard can't drift on this the way the button styling once did.
export function DescriptionDisclosure({description}: {readonly description: string}) {
  const [expanded, setExpanded] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const descriptionId = useId();

  useLayoutEffect(() => {
    const el = descriptionRef.current;
    setTruncated(Boolean(el && el.scrollHeight > el.clientHeight));
  }, [description]);

  return (
    <div className="flex flex-col items-start gap-0.5">
      <p
        ref={descriptionRef}
        id={descriptionId}
        className={cn(
          'text-muted-foreground text-xs',
          expanded ? 'max-h-48 overflow-y-auto whitespace-pre-wrap' : 'line-clamp-2'
        )}>
        {description}
      </p>
      {truncated || expanded ? (
        <Button
          type="button"
          variant="link"
          size="xs"
          aria-expanded={expanded}
          aria-controls={descriptionId}
          className="text-primary-ink h-auto p-0"
          onClick={() => {
            setExpanded((e) => !e);
          }}>
          {expanded ? 'See less' : 'See more'}
        </Button>
      ) : null}
    </div>
  );
}

// The card's fact grid: eyebrow label over value, as many columns as fit at 140px each, so the
// same markup serves the collapsed summary and the expanded panel.
export function MetaGrid({className, ...props}: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-x-4 gap-y-3',
        className
      )}
      {...props}
    />
  );
}

// One label/value pair in a MetaGrid, as the design system's Item: the label is the title and the
// value its description. The reference shape for the key/value layouts elsewhere in the app, which
// each hand-roll their own. Padding comes off because the cell sits in a grid that already spaces
// its cells, and an inset first row would no longer line up with the rule above it. `className`
// carries the grid placement (col-span-full and the like).
export function DetailField({
  label,
  children,
  className
}: {
  readonly label: string;
  readonly children: ReactNode;
  readonly className?: string;
}) {
  return (
    <Item size="xs" className={cn('p-0', className)}>
      <ItemContent>
        <ItemTitle className="text-muted-foreground text-[10px] tracking-widest uppercase">
          {label}
        </ItemTitle>
        <ItemDescription className="text-foreground break-words">{children}</ItemDescription>
      </ItemContent>
    </Item>
  );
}

// A comma-joined-on-the-wire list (tags, keywords, topic categories) as one outline badge each.
export function TagList({items}: {readonly items: readonly string[]}) {
  return (
    <span className="flex flex-wrap gap-1">
      {items.map((item) => (
        <Badge key={item} variant="outline" className="h-auto max-w-full whitespace-normal">
          {item}
        </Badge>
      ))}
    </span>
  );
}

// The number of prior submissions this page already fetched for the entity, worded so "0" reads as
// a fact rather than an empty cell.
export function PriorSubmissions({count}: {readonly count: number}) {
  if (count === 0) {
    return <span className="text-muted-foreground">None</span>;
  }

  return <span>{count}</span>;
}
